import React, { useState, useRef, useEffect } from 'react';
import { HiPaperAirplane, HiOutlineUser, HiPlus, HiOutlineClipboardDocument } from 'react-icons/hi2';
import { RiRobot2Line, RiCheckboxBlankCircleFill } from 'react-icons/ri';

interface Message {
  id: string;
  content: string;
  role: 'user' | 'assistant';
  timestamp: Date;
  thinking?: string;
  response?: string;
}

const parseAssistantMessage = (content: string): { thinking?: string; response?: string; plain?: string } => {
  const thinkingMatch = content.match(/<thinking>([\s\S]*?)<\/thinking>/);
  const responseMatch = content.match(/<response>([\s\S]*?)<\/response>/);

  if (thinkingMatch || responseMatch) {
    // Remove as tags thinking e response do conteúdo
    let remainingContent = content
      .replace(/<thinking>[\s\S]*?<\/thinking>/g, '')
      .replace(/<response>[\s\S]*?<\/response>/g, '')
      .trim();

    return {
      thinking: thinkingMatch ? thinkingMatch[1].trim() : undefined,
      response: responseMatch ? responseMatch[1].trim() : (remainingContent || undefined)
    };
  }

  // Se não tem tags, retorna o conteúdo limpo de possíveis tags soltas
  const cleanContent = content.replace(/<\/?(?:thinking|response)>/g, '').trim();
  return { plain: cleanContent };
};

const Chat: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [serverStatus, setServerStatus] = useState<'online' | 'offline' | 'checking'>('checking');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const checkServerStatus = async () => {
    try {
      const response = await fetch('http://localhost:8000/health');
      setServerStatus(response.ok ? 'online' : 'offline');
    } catch {
      setServerStatus('offline');
    }
  };

  useEffect(() => {
    checkServerStatus();
    const interval = setInterval(checkServerStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const newChat = () => {
    setMessages([]);
    setInput('');
  };

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      content: input,
      role: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    const userInput = input;
    setInput('');
    setIsLoading(true);

    // Create placeholder for assistant message with status
    const assistantId = (Date.now() + 1).toString();
    const assistantMessage: Message = {
      id: assistantId,
      content: '',
      role: 'assistant',
      timestamp: new Date(),
      thinking: 'Processando...',
      response: ''
    };
    setMessages(prev => [...prev, assistantMessage]);

    try {
      const response = await fetch('http://localhost:8000/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ message: userInput })
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let toolCalls: Array<{ tool: string, input?: any, result?: string }> = [];

      while (reader) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const eventData = JSON.parse(line.slice(6));

            if (eventData.type === 'tool_start') {
              toolCalls.push({ tool: eventData.tool, input: eventData.input });
              setMessages(prev => prev.map(msg =>
                msg.id === assistantId
                  ? { ...msg, thinking: toolCalls.map(t => `🔧 ${t.tool}${t.input ? ` → ${JSON.stringify(t.input).slice(0, 50)}...` : ''}`).join('\n') }
                  : msg
              ));
            } else if (eventData.type === 'tool_end') {
              if (toolCalls.length > 0) {
                toolCalls[toolCalls.length - 1].result = eventData.result;
              }
            } else if (eventData.type === 'final') {
              const parsed = parseAssistantMessage(eventData.response || '');
              setMessages(prev => prev.map(msg =>
                msg.id === assistantId
                  ? {
                    ...msg,
                    content: eventData.response,
                    thinking: parsed.thinking,
                    response: parsed.response || parsed.plain
                  }
                  : msg
              ));
            } else if (eventData.type === 'error') {
              setMessages(prev => prev.map(msg =>
                msg.id === assistantId
                  ? { ...msg, thinking: undefined, response: `Erro: ${eventData.message}` }
                  : msg
              ));
            }
          }
        }
      }
    } catch (error) {
      setMessages(prev => prev.map(msg =>
        msg.id === assistantId
          ? { ...msg, thinking: undefined, response: 'Erro ao conectar com o servidor. Verifique se o serviço está rodando.' }
          : msg
      ));
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 p-4 flex justify-between items-center">
        <h1 className="text-xl font-semibold text-gray-800">Strands Chat</h1>
        <div className="flex items-center space-x-3">
          <button
            onClick={newChat}
            className="flex items-center space-x-1 px-3 py-1 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm"
          >
            <HiPlus size={14} />
            <span>Novo Chat</span>
          </button>
          <div className="flex items-center space-x-1">
            <RiCheckboxBlankCircleFill
              size={12}
              className={`${serverStatus === 'online' ? 'text-green-500 fill-green-500' :
                  serverStatus === 'offline' ? 'text-red-500 fill-red-500' :
                    'text-yellow-500 fill-yellow-500'
                }`}
            />
            <span className="text-xs text-gray-600">
              {serverStatus === 'online' ? 'Online' :
                serverStatus === 'offline' ? 'Offline' : 'Verificando...'}
            </span>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 && (
          <div className="text-center text-gray-500 mt-8">
            <RiRobot2Line className="mx-auto mb-4 h-12 w-12 text-gray-400" />
            <p>Olá! Como posso ajudá-lo hoje?</p>
          </div>
        )}

        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`flex max-w-xs lg:max-w-md xl:max-w-lg ${message.role === 'user' ? 'flex-row-reverse' : 'flex-row'
                }`}
            >
              <div
                className={`flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center ${message.role === 'user'
                    ? 'bg-blue-500 text-white ml-2'
                    : 'bg-gray-300 text-gray-600 mr-2'
                  }`}
              >
                {message.role === 'user' ? <HiOutlineUser size={16} /> : <RiRobot2Line size={16} />}
              </div>
              <div
                className={`px-4 py-2 rounded-lg relative ${message.role === 'user'
                    ? 'bg-blue-500 text-white'
                    : 'bg-white text-gray-800 border border-gray-200'
                  }`}
              >
                <button
                  onClick={() => copyToClipboard(message.role === 'assistant' ? (message.response || message.content) : message.content)}
                  className={`absolute top-2 right-2 p-1 rounded hover:bg-opacity-20 ${message.role === 'user' ? 'hover:bg-white' : 'hover:bg-gray-300'
                    }`}
                  title="Copiar"
                >
                  <HiOutlineClipboardDocument size={14} className={message.role === 'user' ? 'text-white' : 'text-gray-600'} />
                </button>
                {message.role === 'assistant' && message.thinking && (
                  <div className="mb-3 pb-3 border-b border-gray-200 pr-6">
                    <div className="flex items-center mb-1">
                      <span className="text-xs font-semibold text-gray-500 uppercase">Raciocínio</span>
                    </div>
                    <p className="text-sm text-gray-600 italic whitespace-pre-wrap">{message.thinking}</p>
                  </div>
                )}
                <p className="text-sm whitespace-pre-wrap pr-6">
                  {message.role === 'assistant' ? message.response : message.content}
                </p>
                <p className="text-xs mt-1 opacity-70">
                  {message.timestamp.toLocaleTimeString()}
                </p>
              </div>
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="flex justify-start">
            <div className="flex flex-row">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-gray-300 text-gray-600 mr-2 flex items-center justify-center">
                <RiRobot2Line size={16} />
              </div>
              <div className="bg-white text-gray-800 border border-gray-200 px-4 py-2 rounded-lg">
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <div className="bg-white border-t border-gray-200 p-4">
        <div className="flex space-x-2 items-end">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Digite sua mensagem..."
            className="flex-1 resize-none border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent max-h-24 overflow-y-auto"
            rows={1}
            disabled={isLoading}
            style={{
              minHeight: '40px',
              height: 'auto',
              maxHeight: '96px'
            }}
            onInput={(e) => {
              const target = e.target as HTMLTextAreaElement;
              target.style.height = 'auto';
              target.style.height = Math.min(target.scrollHeight, 96) + 'px';
            }}
          />
          <button
            onClick={sendMessage}
            disabled={!input.trim() || isLoading}
            className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center h-10"
          >
            <HiPaperAirplane size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Chat;
